/**
 * Workspace member invite dialog — Iter 3.
 * Picks a user from the org roster and grants membership on a
 * (engagement, workspace) tuple.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
    DialogDescription,
} from "../../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../../components/ui/select";
import { toast } from "sonner";


export default function WorkspaceInviteDialog({
    open, onClose, onInvited, engagementId, workspaceCode,
}) {
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState("");
    const [roleLabel, setRoleLabel] = useState("member");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!open) return;
        setUserId(""); setRoleLabel("member"); setReason(""); setSearch("");
        (async () => {
            try {
                // Reuse the project meta endpoint to list eligible users.
                // UAT 2026-07-28 defect follow-up — backend caps `limit` at
                // le=50 (see routes_projects.eligible_users). Requesting
                // more triggered a Pydantic 422 immediately on dialog open.
                // 50 candidates is sufficient given the search filter below.
                const r = await api.get(`/projects/_meta/eligible-users?purpose=workspace_invite&limit=50`);
                setUsers(r.data?.items || []);
            } catch (err) {
                toast.error(formatApiError(err));
            }
        })();
    }, [open]);

    const filtered = search.trim()
        ? users.filter((u) =>
            (u.name || "").toLowerCase().includes(search.toLowerCase()) ||
            (u.email || "").toLowerCase().includes(search.toLowerCase()),
        )
        : users;

    const submit = async () => {
        if (!userId) return toast.error("Pick a user.");
        setSaving(true);
        try {
            await api.post(
                `/projects/engagement/${engagementId}/workspace/${workspaceCode}/member`,
                {
                    user_id: userId,
                    role_label: roleLabel,
                    reason: reason.trim() || null,
                },
            );
            toast.success("Invitation granted.");
            onInvited?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? null : onClose?.())}>
            <DialogContent className="max-w-lg" data-testid="workspace-invite-dialog">
                <DialogHeader>
                    <DialogTitle>Invite to {workspaceCode || "workspace"}</DialogTitle>
                    <DialogDescription>
                        Workspace membership is independent of project participation —
                        each invite is captured in the audit trail with granted-by / granted-at.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 py-2">
                    <div>
                        <Label>Search users</Label>
                        <Input
                            data-testid="workspace-invite-search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Filter by name or email"
                        />
                    </div>
                    <div>
                        <Label>User</Label>
                        <Select value={userId} onValueChange={setUserId}>
                            <SelectTrigger data-testid="workspace-invite-user-trigger">
                                <SelectValue placeholder="Pick a user..." />
                            </SelectTrigger>
                            <SelectContent>
                                {filtered.slice(0, 50).map((u) => (
                                    <SelectItem key={u.id} value={u.id} data-testid={`workspace-invite-user-option-${u.id}`}>
                                        {(u.name || u.email || u.id)} · {u.role}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Role label</Label>
                        <Input
                            data-testid="workspace-invite-role-label"
                            value={roleLabel}
                            onChange={(e) => setRoleLabel(e.target.value)}
                            placeholder="e.g. reviewer, contributor, member"
                        />
                    </div>
                    <div>
                        <Label>Reason (optional)</Label>
                        <Textarea
                            data-testid="workspace-invite-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Justification is stored on the membership record for audit."
                            rows={2}
                        />
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="workspace-invite-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={saving || !userId}
                        data-testid="workspace-invite-submit"
                    >
                        {saving ? "Granting..." : "Grant membership"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
